import { ReactElement, SyntheticEvent, useEffect, useState } from 'react'
import {
  SectionTitle,
  PageWrapper,
  FlexWrapper,
  Input,
  Button,
  EditableDropdown,
  Loader
} from '../../../../components'
import { useHistory } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import strings from '../../../../locale/en'
import SecondaryButton from 'react-bootstrap/Button'
import CourseSelection from '../CourseSelection'
import { statusList } from './const'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import {
  AdditionalCourse,
  Institute
} from '../../../../redux/coachingCenter/types'
import { InstituteField } from '../typings'
import { hasFormError } from '../../../../helpers/formValidation'
import {
  editInstitute as edit,
  addInstitute as add,
  getCourses,
  resetInstitute
} from '../../../../redux/coachingCenter/actions'
import { validateInstituteForm } from '../helpers'
import ROUTES from '../../../../const/routes'
import { DatePickerWrapper } from '../subcomponents'
import { DATE_FORMAT_YYYYMMDD } from '../../../../const/dateFormat'
import { format } from 'date-fns'

const AddInstitute = (): ReactElement => {
  const { isLoading, institute } = useSelector(
    (state: RootState) => ({
      isLoading: state.coachingCenter.isLoading,
      institute: state.coachingCenter.selectedInstitute as Institute
    }),
    shallowEqual
  )
  const [values, setValues] = useState(institute || {})
  const [errors, setErrors] = useState({} as Record<string, string>)

  const dispatch = useDispatch()
  const history = useHistory()
  const isEdit = history.location.pathname !== ROUTES.ADD_INSTITUTE

  const {
    userDetail: { profileImage },
    button: { save, cancel },
    menu: {
      admin: { addInstitute, editInstitute, instituteName }
    },
    instituteDetails: {
      mobileNumberLabel,
      emailLabel,
      address1Label,
      address2Label,
      countryLabel,
      stateLabel,
      zipcodeLabel,
      cityLabel,
      statusLabel,
      institueCodeLabel,
      institueExpiresOnLabel,
      questionLimitLabel,
      studentCreationLimitLabel,
      teacherCreationLimitLabel,
      selectOptionLabel
    }
  } = strings

  const {
    coachingCentreName,
    mobileNumber,
    email,
    address1,
    address2,
    country,
    state,
    city,
    zipCode,
    expiryOn,
    additionalCourses,
    status,
    coachingCenterCode,
    questionLimit,
    studentCount,
    teacherCount,
    logoUrl,
    id
  } = values || {}

  const validateField = (field: InstituteField): void => {
    setErrors(
      validateInstituteForm({
        values,
        errors,
        field
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    const validationError = validateInstituteForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      const formData = new FormData()
      formData.append('coachingCentreName', coachingCentreName)
      formData.append('mobileNumber', mobileNumber)
      formData.append('email', email)
      formData.append('address1', address1)
      formData.append('address2', address2)
      formData.append('country', country)
      formData.append('state', state)
      formData.append('city', city)
      formData.append('zipCode', zipCode)
      formData.append('expiryOn', expiryOn)
      formData.append('additionalCourses', JSON.stringify(additionalCourses))
      formData.append('status', status)
      formData.append('coachingCenterCode', coachingCenterCode)
      formData.append('questionLimit', questionLimit)
      formData.append('studentCount', studentCount)
      formData.append('teacherCount', teacherCount)
      formData.append('logo', logoUrl || '')
      isEdit
        ? dispatch(
            edit({
              requestPayload: formData,
              coachingCentreId: id
            })
          )
        : dispatch(add(formData))
    }
  }

  useEffect(() => {
    dispatch(getCourses())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    !isEdit && setValues({} as Institute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  useEffect(() => {
    institute && isEdit && setValues(institute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institute])

  return isLoading ? (
    <Loader />
  ) : (
    <PageWrapper id="container">
      <SectionTitle title={isEdit ? editInstitute : addInstitute} hasBorder />
      <FlexWrapper justifyContent="space-between">
        <Input
          label={instituteName}
          isRequired
          placeholder={instituteName}
          value={values?.coachingCentreName || ''}
          name="instituteName"
          onBlur={() => validateField('coachingCentreName')}
          error={errors.coachingCentreName}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, coachingCentreName: value })
          }
        />
        <Input
          label={mobileNumberLabel}
          isRequired
          placeholder={mobileNumberLabel}
          value={values?.mobileNumber || ''}
          name="mobileNumber"
          onBlur={() => validateField('mobileNumber')}
          error={errors.mobileNumber}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, mobileNumber: value })
          }
        />
        <Input
          label={emailLabel}
          placeholder={emailLabel}
          value={values?.email || ''}
          name="email"
          width="30%"
          onChange={(value: string) => setValues({ ...values, email: value })}
        />
        <Input
          label={address1Label}
          isRequired
          placeholder={address1Label}
          value={values?.address1 || ''}
          name="address1"
          onBlur={() => validateField('address1')}
          error={errors.address1}
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, address1: value })
          }
        />
        <Input
          label={address2Label}
          placeholder={address2Label}
          value={values?.address2 || ''}
          name="address2"
          width="30%"
          onChange={(value: string) =>
            setValues({ ...values, address2: value })
          }
        />
        <Input
          label={countryLabel}
          isRequired
          placeholder={countryLabel}
          value={values?.country || ''}
          name="country"
          onBlur={() => validateField('country')}
          error={errors.country}
          width="30%"
          onChange={(value: string) => setValues({ ...values, country: value })}
        />
        <Input
          label={stateLabel}
          isRequired
          placeholder={stateLabel}
          value={values?.state || ''}
          name="state"
          onBlur={() => validateField('state')}
          error={errors.state}
          width="30%"
          onChange={(value: string) => setValues({ ...values, state: value })}
        />
        <Input
          label={cityLabel}
          isRequired
          placeholder={cityLabel}
          name="city"
          width="30%"
          value={values?.city || ''}
          onBlur={() => validateField('city')}
          error={errors.city}
          onChange={(value: string) => setValues({ ...values, city: value })}
        />
        <Input
          label={zipcodeLabel}
          isRequired
          placeholder={zipcodeLabel}
          name="zipcode"
          width="30%"
          value={values?.zipCode || ''}
          onBlur={() => validateField('zipCode')}
          error={errors.zipCode}
          onChange={(value: string) => setValues({ ...values, zipCode: value })}
        />
        <Input
          label={institueCodeLabel}
          isRequired
          placeholder={institueCodeLabel}
          name="institueCode"
          width="30%"
          value={values?.coachingCenterCode || ''}
          onBlur={() => validateField('coachingCenterCode')}
          error={errors.coachingCenterCode}
          onChange={(value: string) =>
            setValues({ ...values, coachingCenterCode: value })
          }
        />
        <DatePickerWrapper>
          <DatePicker
            selected={
              values?.expiryOn ? new Date(values?.expiryOn) : new Date()
            }
            onSelect={(date: Date) =>
              setValues({
                ...values,
                expiryOn: date ? format(date, DATE_FORMAT_YYYYMMDD) : ''
              })
            }
            onChange={(date: Date) =>
              setValues({
                ...values,
                expiryOn: date ? format(date, DATE_FORMAT_YYYYMMDD) : ''
              })
            }
            placeholderText={institueExpiresOnLabel}
            customInput={
              <Input
                value={values?.expiryOn || ''}
                inputType="text"
                label={institueExpiresOnLabel}
                placeholder={institueExpiresOnLabel}
                suffix={['far', 'calendar']}
              />
            }
          />
        </DatePickerWrapper>
        <Input
          label={questionLimitLabel}
          placeholder={questionLimitLabel}
          name="questionLimit"
          width="30%"
          value={values?.questionLimit || ''}
          onChange={(value: string) =>
            setValues({ ...values, questionLimit: value })
          }
        />
        <CourseSelection
          isEdit={isEdit}
          onSelect={(additionalCourses: Array<AdditionalCourse>) => {
            setValues({ ...values, additionalCourses })
          }}
        />
        <Input
          label={profileImage}
          width="30%"
          inputType="file"
          placeholder={profileImage}
          value=""
          onChange={(value: string) => setValues({ ...values, logoUrl: value })}
        />
        <EditableDropdown
          width="30%"
          title={statusLabel}
          isRequired
          dropdownList={statusList}
          placeholder={selectOptionLabel}
          handleSelect={(selectedOption) => {
            setValues({ ...values, status: selectedOption.name })
            setErrors(
              validateInstituteForm({
                values: { ...values, status: selectedOption.name },
                errors,
                field: 'status'
              })
            )
          }}
          onBlur={() => validateField('status')}
          error={errors.status}
          defaultValue={{
            id: values?.status === 'ACTIVE' ? '01' : '02',
            name: values?.status
          }}
        />
        <Input
          label={studentCreationLimitLabel}
          placeholder={studentCreationLimitLabel}
          isRequired
          name="studentCount"
          width="30%"
          value={values?.studentCount || ''}
          onBlur={() => validateField('studentCount')}
          error={errors.studentCount}
          onChange={(value: string) =>
            setValues({ ...values, studentCount: value })
          }
        />
        <Input
          label={teacherCreationLimitLabel}
          placeholder={teacherCreationLimitLabel}
          isRequired
          name="teacherCount"
          width="30%"
          value={values?.teacherCount || ''}
          onBlur={() => validateField('teacherCount')}
          error={errors.teacherCount}
          onChange={(value: string) =>
            setValues({ ...values, teacherCount: value })
          }
        />
      </FlexWrapper>
      <FlexWrapper justifyContent="center">
        <SecondaryButton
          variant="outline-secondary"
          onClick={() => {
            dispatch(resetInstitute())
            history.goBack()
          }}
        >
          {cancel}
        </SecondaryButton>
        <Button
          onClick={(e: SyntheticEvent) => {
            dispatch(resetInstitute())
            handleSubmit(e)
          }}
        >
          {save}
        </Button>
      </FlexWrapper>
    </PageWrapper>
  )
}

export default AddInstitute
